import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import { getEzyVetStatus } from "@/api/integrations";
import { getEzyVetTelemetry } from "@/api/ezyvet";
import {
  EzyVetQuotaContext,
  readingFromHeaders,
  readingFromTelemetry,
  type EzyVetQuotaState,
  type QuotaReading,
} from "@/context/ezyvetQuota";
import type { ApiError, EzyVetStatus } from "@/types";
import type { EzyVetTelemetry } from "@/types/ezyvet";

const isMissing = (err: ApiError | null) => err?.status === 404 || err?.status === 405;

/**
 * Real-time ezyVet quota and token state for the signed-in clinic.
 *
 * Two inputs, newest wins: the telemetry endpoint (polled every 15 s) and any
 * x-ratelimit-* headers the backend forwards on ordinary API responses. Token
 * expiry comes from the connection status the settings page already caches.
 */
export function EzyVetQuotaProvider({ enabled, children }: { enabled: boolean; children: ReactNode }) {
  const [fromHeaders, setFromHeaders] = useState<QuotaReading | null>(null);

  useEffect(() => {
    if (!enabled) return;
    const id = apiClient.interceptors.response.use((res) => {
      const reading = readingFromHeaders(res);
      if (reading) setFromHeaders(reading);
      return res;
    });
    return () => apiClient.interceptors.response.eject(id);
  }, [enabled]);

  const telemetry = useQuery<EzyVetTelemetry, ApiError>({
    queryKey: ["ezyvet", "telemetry"],
    queryFn: getEzyVetTelemetry,
    enabled,
    // Stop polling an endpoint the backend doesn't have
    refetchInterval: (q) => (isMissing(q.state.error) ? false : 15_000),
    staleTime: 5_000,
  });

  const status = useQuery<EzyVetStatus, ApiError>({
    queryKey: ["integrations", "ezyvet"],
    queryFn: getEzyVetStatus,
    enabled,
  });

  const { refetch: refetchTelemetry } = telemetry;
  const { refetch: refetchStatus } = status;

  const value = useMemo<EzyVetQuotaState>(() => {
    const fromTelemetry = telemetry.data ? readingFromTelemetry(telemetry.data, telemetry.dataUpdatedAt) : null;
    const quota =
      fromHeaders && (!fromTelemetry || fromHeaders.observedAt >= fromTelemetry.observedAt) ? fromHeaders : fromTelemetry;
    const expires = status.data?.token_expires_at ? Date.parse(status.data.token_expires_at) : null;
    const expiresFromTelemetry = telemetry.data
      ? telemetry.dataUpdatedAt + telemetry.data.token_ttl_seconds * 1000
      : null;
    const quotaError = telemetry.error ?? null;

    return {
      quota,
      quotaAvailable: quota !== null || !isMissing(quotaError),
      quotaError,
      connected: Boolean(status.data?.connected ?? telemetry.data?.connected),
      tokenExpiresAt: expires ?? expiresFromTelemetry,
      refresh: () => {
        void refetchTelemetry();
        void refetchStatus();
      },
    };
  }, [fromHeaders, telemetry.data, telemetry.dataUpdatedAt, telemetry.error, status.data, refetchTelemetry, refetchStatus]);

  return <EzyVetQuotaContext.Provider value={value}>{children}</EzyVetQuotaContext.Provider>;
}
